import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Search, AlertCircle, Loader2 } from 'lucide-react';
import type { Asset } from '@/types/asset';
import type { AssetSelectionFilter } from '@/types/depreciation';
import { dataService } from '@/services/data-service';

interface AssetSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (selectedAssets: Asset[]) => void;
  title?: string;
  description?: string;
  excludeAssetIds?: string[]; // Asset IDs to exclude from selection (already in detail grid)
}

export function AssetSelectionDialog({ 
  open, 
  onOpenChange, 
  onConfirm,
  title = "Select Assets for Depreciation",
  description = "Choose one or more assets, or select all. You can refine the set later.",
  excludeAssetIds = []
}: AssetSelectionDialogProps): React.ReactElement {
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<AssetSelectionFilter>({
    status: ['active'],
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [assets, setAssets] = useState<Asset[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [availableLocations, setAvailableLocations] = useState<string[]>([]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load available categories and locations when dialog opens
  useEffect(() => {
    if (open) {
      loadFilterOptions();
    }
  }, [open]);

  // Fetch assets from data service
  useEffect(() => {
    if (open) {
      loadAssets();
    }
  }, [open, filters, debouncedSearchTerm]);

  const loadFilterOptions = async () => {
    try {
      // Fetch all assets to extract unique categories and locations
      const response = await dataService.getAssets({
        page: 1,
        pageSize: 10000,
      });
      
      // Extract unique categories and locations
      const categories = Array.from(new Set(response.data.map((asset: any) => asset.category).filter(Boolean))) as string[];
      const locations = Array.from(new Set(response.data.map((asset: any) => asset.location).filter(Boolean))) as string[];
      
      setAvailableCategories(categories.sort());
      setAvailableLocations(locations.sort());
    } catch (err) {
      console.error('Failed to load filter options:', err);
      // Set fallback values if loading fails
      setAvailableCategories(['Computer Equipment', 'Furniture & Fixtures', 'Vehicles', 'Office Equipment', 'Manufacturing Equipment']);
      setAvailableLocations(['London HQ', 'Manchester Office', 'Birmingham Warehouse', 'Edinburgh Branch']);
    }
  };

  const loadAssets = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await dataService.getAssets({
        status: filters.status?.[0],
        category: filters.category?.[0],
        location: filters.location?.[0],
        search: debouncedSearchTerm || undefined,
        page: 1,
        pageSize: 10000, // Get all assets for selection
      });
      setAssets(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load assets');
      setAssets([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter out assets that are already in the detail grid
  const filteredAssets = assets.filter((asset: any) => !excludeAssetIds.includes(asset.id));

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedAssetIds(new Set(filteredAssets.map((a) => a.id)));
    } else {
      setSelectedAssetIds(new Set());
    }
  };

  const handleSelectAsset = (assetId: string, checked: boolean) => {
    const newSet = new Set(selectedAssetIds);
    if (checked) {
      newSet.add(assetId);
    } else {
      newSet.delete(assetId);
    }
    setSelectedAssetIds(newSet);
  };

  const handleConfirm = () => {
    const selected = assets.filter((a) => selectedAssetIds.has(a.id));
    onConfirm(selected);
    // Reset state
    setSelectedAssetIds(new Set());
    setSearchTerm('');
  };

  const allSelected = filteredAssets.length > 0 && filteredAssets.every((a) => selectedAssetIds.has(a.id));
  const canProceed = selectedAssetIds.size > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Asset ID, Name, Serial..."
                  value={searchTerm}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select
                value={filters.category?.[0] || 'all'}
                onValueChange={(value: string) =>
                  setFilters({ ...filters, category: value === 'all' ? undefined : [value] })
                }
              >
                <SelectTrigger id="category">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {availableCategories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Select
                value={filters.location?.[0] || 'all'}
                onValueChange={(value: string) =>
                  setFilters({ ...filters, location: value === 'all' ? undefined : [value] })
                }
              >
                <SelectTrigger id="location">
                  <SelectValue placeholder="All locations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All locations</SelectItem>
                  {availableLocations.map((location) => (
                    <SelectItem key={location} value={location}>
                      {location}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Selection Info */}
          <div className="flex items-center justify-between py-2 border-y">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="select-all"
                  checked={allSelected}
                  onCheckedChange={handleSelectAll}
                />
                <Label htmlFor="select-all" className="cursor-pointer">
                  Select all ({filteredAssets.length})
                </Label>
              </div>
              <Badge variant="secondary">
                {selectedAssetIds.size} selected
              </Badge>
            </div>
            <div className="text-sm text-muted-foreground">
              Showing {filteredAssets.length} assets
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Asset List */}
          <div className="flex-1 overflow-auto border rounded-lg min-h-[400px]">
            {isLoading ? (
              <div className="divide-y">
                {/* Loading skeleton - show 5 placeholder rows */}
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-4 p-4 animate-pulse">
                    <div className="w-4 h-4 bg-muted rounded" />
                    <div className="w-16 h-16 bg-muted rounded-md" />
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-2">
                      <div className="space-y-2">
                        <div className="h-4 bg-muted rounded w-20" />
                        <div className="h-3 bg-muted rounded w-32" />
                      </div>
                      <div className="space-y-2">
                        <div className="h-3 bg-muted rounded w-16" />
                        <div className="h-4 bg-muted rounded w-24" />
                      </div>
                      <div className="space-y-2">
                        <div className="h-3 bg-muted rounded w-16" />
                        <div className="h-4 bg-muted rounded w-28" />
                      </div>
                      <div className="space-y-2">
                        <div className="h-3 bg-muted rounded w-20" />
                        <div className="h-4 bg-muted rounded w-16" />
                      </div>
                      <div className="space-y-2">
                        <div className="h-6 bg-muted rounded w-16" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredAssets.length === 0 ? (
              <div className="flex items-center justify-center h-full min-h-[400px] text-muted-foreground">
                No assets match the current filters
              </div>
            ) : (
              <div className="divide-y">
                {filteredAssets.map((asset) => (
                  <div
                    key={asset.id}
                    className="flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors"
                  >
                    <Checkbox
                      checked={selectedAssetIds.has(asset.id)}
                      onCheckedChange={(checked) => handleSelectAsset(asset.id, checked as boolean)}
                    />
                    {/* Thumbnail */}
                    <div className="flex-shrink-0">
                      {asset.imageUrl ? (
                        <img
                          src={asset.imageUrl}
                          alt={asset.name}
                          className="w-16 h-16 object-cover rounded-md border"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-muted rounded-md border flex items-center justify-center">
                          <span className="text-xs text-muted-foreground">No image</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-2">
                      <div>
                        <div className="font-medium">{asset.assetId}</div>
                        <div className="text-sm text-muted-foreground">{asset.name}</div>
                      </div>
                      <div className="text-sm">
                        <div className="text-muted-foreground">Category</div>
                        <div>{asset.category}</div>
                      </div>
                      <div className="text-sm">
                        <div className="text-muted-foreground">Location</div>
                        <div>{asset.location}</div>
                      </div>
                      <div className="text-sm">
                        <div className="text-muted-foreground">Opening NBV</div>
                        <div>£{asset.netBookValue.toFixed(2)}</div>
                      </div>
                      <div className="text-sm">
                        <Badge
                          variant={
                            asset.status === 'active'
                              ? 'default'
                              : asset.status === 'maintenance'
                              ? 'secondary'
                              : 'outline'
                          }
                        >
                          {asset.status}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!canProceed}>
            Add to Run ({selectedAssetIds.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
